/**
 * useAuthToken Hook
 * Retrieves the current user's Supabase authentication token
 *
 * Usage:
 * ```tsx
 * const { getAuthToken } = useAuthToken()
 * const token = await getAuthToken()
 * ```
 */

import { supabase } from '../lib/supabase'
import { useCallback } from 'react'
import { logError } from '../utils/logger'

export const useAuthToken = () => {
  /**
   * Get the current user's authentication token
   * Returns null if user is not authenticated
   */
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    try {
      const { data, error } = await supabase.auth.getSession()

      if (error) {
        logError('useAuthToken', 'Failed to get session', { error })
        return null
      }

      const token = data.session?.access_token
      if (!token) {
        logError('useAuthToken', 'No access token in session')
        return null
      }

      return token
    } catch (error) {
      logError('useAuthToken', 'Error retrieving auth token', { error })
      return null
    }
  }, [])

  return { getAuthToken }
}
